import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Table, TableCard } from '@/components/application/table/table';
import { ContainerSpreadsheetCell } from './container-spreadsheet-cell';
import { ContainerRealtimeIndicator } from './container-realtime-indicator';
import { ContainerEditModal } from './container-edit-modal';
import type { Container } from '@/types/container';
import { useTranslation } from 'react-i18next';
import { containerService } from '@/data/mock-containers';

interface ContainerSpreadsheetViewProps {
    containers: Container[];
    totalCount?: number; // Numero totale di container (per lazy loading)
    onUpdate?: (containerId: string, field: string, value: any) => void;
    onContainerClick?: (containerId: string) => void;
    editableFields?: string[];
    className?: string;
}

export const ContainerSpreadsheetView = ({
    containers,
    totalCount,
    onUpdate,
    onContainerClick,
    className
}: ContainerSpreadsheetViewProps) => {
    const { t } = useTranslation(['pages:containers', 'common']);
    const [editingCell, setEditingCell] = useState<{ containerId: string; field: string } | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [lastUpdateTime, setLastUpdateTime] = useState<Date>(new Date());
    const [updatedCells, setUpdatedCells] = useState<Set<string>>(new Set());
    const [containersState, setContainersState] = useState<Container[]>(containers);
    // Usa useRef per tracciare i container in editing senza causare re-render
    const editingContainersRef = useRef<Set<string>>(new Set());

    // Sincronizza stato quando containers cambiano (ma non se sono in editing)
    useEffect(() => {
        setContainersState(prev => {
            // Mantieni i container in editing con i loro valori locali
            // Per gli altri, aggiorna con i nuovi dati
            const editingIds = editingContainersRef.current;
            return containers.map(newContainer => {
                const existingContainer = prev.find(c => c.id === newContainer.id);
                // Se il container è in editing, mantieni lo stato locale
                if (existingContainer && editingIds.has(newContainer.id)) {
                    return existingContainer;
                }
                // Altrimenti usa i nuovi dati
                return newContainer;
            });
        });
    }, [containers]);

    // Simulazione aggiornamenti realtime ogni 5-10 secondi
    useEffect(() => {
        const interval = setInterval(() => {
            setContainersState(prev => {
                const updated = prev.map(container => {
                    // Non aggiornare se il container ha una cella in editing
                    if (editingContainersRef.current.has(container.id)) {
                        return container;
                    }

                    const changes: Partial<Container> = {};
                    const cellKey = `${container.id}-progress`;

                    // Aggiorna progress (incremento graduale)
                    if (Math.random() > 0.7) {
                        const newProgress = Math.min(100, container.progress + Math.floor(Math.random() * 3));
                        if (newProgress !== container.progress) {
                            changes.progress = newProgress;
                            setUpdatedCells(prev => new Set(prev).add(cellKey));
                            setTimeout(() => {
                                setUpdatedCells(prev => {
                                    const next = new Set(prev);
                                    next.delete(cellKey);
                                    return next;
                                });
                            }, 3000);
                        }
                    }

                    // Occasionalmente aggiorna ETA (piccole variazioni)
                    if (Math.random() > 0.9) {
                        const currentEta = new Date(container.eta);
                        const daysOffset = Math.floor(Math.random() * 3) - 1; // -1, 0, o +1 giorni
                        const newEta = new Date(currentEta);
                        newEta.setDate(newEta.getDate() + daysOffset);
                        changes.eta = newEta;
                        const etaKey = `${container.id}-eta`;
                        setUpdatedCells(prev => new Set(prev).add(etaKey));
                        setTimeout(() => {
                            setUpdatedCells(prev => {
                                const next = new Set(prev);
                                next.delete(etaKey);
                                return next;
                            });
                        }, 3000);
                    }

                    // Occasionalmente aggiorna Document Status
                    if (Math.random() > 0.85) {
                        const currentDocStatus = container.documentStatus;
                        let newDocStatus = currentDocStatus;
                        
                        // Transizioni logiche per document status
                        if (currentDocStatus === 'Pending') {
                            newDocStatus = Math.random() > 0.3 ? 'Completo' : 'Error';
                        } else if (currentDocStatus === 'Error') {
                            newDocStatus = Math.random() > 0.5 ? 'Pending' : 'Completo';
                        } else if (currentDocStatus === 'Completo') {
                            // Raramente torna a Pending o Error
                            if (Math.random() > 0.9) {
                                newDocStatus = Math.random() > 0.5 ? 'Pending' : 'Error';
                            }
                        }
                        
                        if (newDocStatus !== currentDocStatus) {
                            changes.documentStatus = newDocStatus;
                            const docKey = `${container.id}-documentStatus`;
                            setUpdatedCells(prev => new Set(prev).add(docKey));
                            setTimeout(() => {
                                setUpdatedCells(prev => {
                                    const next = new Set(prev);
                                    next.delete(docKey);
                                    return next;
                                });
                            }, 3000);
                        }
                    }

                    // Occasionalmente aggiorna Risk Level
                    if (Math.random() > 0.85) {
                        const currentRisk = container.riskLevel;
                        const riskLevels: Array<'Low' | 'Medium' | 'High' | 'Critical'> = ['Low', 'Medium', 'High', 'Critical'];
                        const currentIndex = riskLevels.indexOf(currentRisk);
                        let newRisk = currentRisk;
                        
                        // Probabilità di peggiorare (60%) vs migliorare (40%)
                        if (Math.random() > 0.4) {
                            // Peggiora (aumenta l'indice)
                            if (currentIndex < riskLevels.length - 1) {
                                newRisk = riskLevels[currentIndex + 1];
                            }
                        } else {
                            // Migliora (diminuisce l'indice)
                            if (currentIndex > 0) {
                                newRisk = riskLevels[currentIndex - 1];
                            }
                        }
                        
                        if (newRisk !== currentRisk) {
                            changes.riskLevel = newRisk;
                            const riskKey = `${container.id}-riskLevel`;
                            setUpdatedCells(prev => new Set(prev).add(riskKey));
                            setTimeout(() => {
                                setUpdatedCells(prev => {
                                    const next = new Set(prev);
                                    next.delete(riskKey);
                                    return next;
                                });
                            }, 3000);
                        }
                    }

                    return Object.keys(changes).length > 0 ? { ...container, ...changes } : container;
                });

                setLastUpdateTime(new Date());
                return updated;
            });
        }, 5000 + Math.random() * 5000); // 5-10 secondi

        return () => clearInterval(interval);
    }, []); // Rimuoviamo editingCell dalla dipendenza per evitare re-render

    const handleStartEdit = useCallback((containerId: string, field: string) => {
        // Non aprire il modale per campi non editabili
        if (['containerNumber', 'route', 'progress'].includes(field)) {
            return;
        }
        setEditingCell({ containerId, field });
        setIsModalOpen(true);
        editingContainersRef.current.add(containerId);
    }, []);

    const handleSave = useCallback((containerId: string, field: string, value: any) => {
        setContainersState(prev => {
            const updated = prev.map(c => {
                if (c.id === containerId) {
                    const updatedContainer = { ...c, [field]: value, updatedAt: new Date() };
                    
                    // Salva nel service
                    const updateData: any = { [field]: value };
                    containerService.update(containerId, updateData);
                    
                    // Chiama callback se disponibile
                    if (onUpdate) {
                        onUpdate(containerId, field, value);
                    }
                    
                    return updatedContainer;
                }
                return c;
            });
            return updated;
        });
        
        setEditingCell(null);
        setIsModalOpen(false);
        editingContainersRef.current.delete(containerId);
    }, [onUpdate]);

    const handleCloseModal = useCallback(() => {
        if (editingCell) {
            editingContainersRef.current.delete(editingCell.containerId);
        }
        setEditingCell(null);
        setIsModalOpen(false);
    }, [editingCell]);

    const columns = useMemo(() => [
        { id: 'containerNumber', label: t('list.table.headers.containerNumber') },
        { id: 'route', label: t('list.table.headers.route') },
        { id: 'shippingCompany', label: 'Shipping Company' },
        { id: 'progress', label: t('list.table.headers.progress') },
        { id: 'eta', label: 'ETA' },
        { id: 'status', label: t('list.table.headers.status') },
        { id: 'documentStatus', label: t('list.table.headers.documentStatus') },
        { id: 'riskLevel', label: t('list.table.headers.riskLevel') },
        { id: 'value', label: t('list.table.headers.value') }
    ], [t]);

    return (
        <div className={className}>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-primary">
                        {t('list.table.title')} {totalCount !== undefined ? `(${containersState.length} di ${totalCount})` : `(${containersState.length})`}
                    </h3>
                    <ContainerRealtimeIndicator lastUpdateTime={lastUpdateTime} />
                </div>
            </div>

            <TableCard.Root>
                <TableCard.Header
                    title={t('list.table.title')}
                    description={t('list.table.description')}
                />

                <Table>
                    <Table.Header>
                        {columns.map((col, index) => (
                            <Table.Head 
                                key={col.id} 
                                label={col.label} 
                                isRowHeader={index === 0}
                            />
                        ))}
                    </Table.Header>
                    <Table.Body>
                        {containersState.map((container) => (
                            <Table.Row key={container.id}>
                                {columns.map((col) => {
                                    const cellKey = `${container.id}-${col.id}`;
                                    const isUpdated = updatedCells.has(cellKey);
                                    
                                    // Ottieni il valore corretto per la colonna
                                    let value: any;
                                    if (col.id === 'route') {
                                        value = container.route;
                                    } else {
                                        value = (container as any)[col.id];
                                    }

                                    return (
                                        <Table.Cell
                                            key={col.id}
                                            className={isUpdated ? 'bg-brand-50 dark:bg-brand-950 transition-colors duration-300' : ''}
                                        >
                                            <ContainerSpreadsheetCell
                                                container={container}
                                                field={col.id}
                                                value={value}
                                                onClick={() => handleStartEdit(container.id, col.id)}
                                                onContainerClick={onContainerClick}
                                            />
                                        </Table.Cell>
                                    );
                                })}
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {/* Modale per editing */}
            {editingCell && (() => {
                const container = containersState.find(c => c.id === editingCell.containerId);
                if (!container) return null;

                let cellValue: any;
                if (editingCell.field === 'route') {
                    cellValue = container.route;
                } else {
                    cellValue = (container as any)[editingCell.field];
                }

                return (
                    <ContainerEditModal
                        container={container}
                        field={editingCell.field}
                        value={cellValue}
                        isOpen={isModalOpen}
                        onClose={handleCloseModal}
                        onSave={(newValue) => handleSave(editingCell.containerId, editingCell.field, newValue)}
                    />
                );
            })()}
        </div>
    );
};

